/**
 * Mostly Copilot generated (change whatever you like)
 * Dev-only profiler for DesignerNode building: tracks total build time, per-node average, max single node build, slow node records, topNodes, hitch bursts.
 * Disabled by default so overhead is a single boolean check when off.
 * Enabled in console via designer/client/src/ui/DesignerProfiling.tsx
 * */

interface SlowNodeRecord {
  id: number;
  typeId: unknown;
  durationMs: number;
  configPhaseMs: number;
  childrenPhaseMs: number;
  childSlotNames: string[];
  timestamp: number;
}

interface HitchRecord {
  timestamp: number;
  windowDurationMs: number;
  buildCount: number;
}

export interface DesignerNodeBuildPerfStats {
  totalBuildTimeMs: number;
  nodeBuildCount: number;
  distinctNodeCount: number;
  averageNodeBuildTimeMs: number;
  maxSingleNodeBuildTimeMs: number;
  lastRootBuildTimeMs: number;
  totalConfigPhaseMs: number;
  totalChildrenPhaseMs: number;
  averageConfigPhaseMs: number;
  averageChildrenPhaseMs: number;
  maxConfigPhaseMs: number;
  maxChildrenPhaseMs: number;
}

export interface DesignerNodeBuildPerfStatsWithSlowNodes extends DesignerNodeBuildPerfStats {
  slowNodes: SlowNodeRecord[];
  topNodes: SlowNodeRecord[];
  hitches: HitchRecord[];
}

class BuildDesignerNodeProfiler {
  private enabled = false;
  private totalBuildTimeMs = 0;
  private nodeBuildCount = 0;
  private maxSingleNodeBuildTimeMs = 0;
  private lastRootBuildTimeMs = 0;
  private totalConfigPhaseMs = 0;
  private totalChildrenPhaseMs = 0;
  private maxConfigPhaseMs = 0;
  private maxChildrenPhaseMs = 0;
  private distinctNodeIds = new Set<number>();
  private slowNodes: SlowNodeRecord[] = [];
  private topNodes: SlowNodeRecord[] = [];
  private hitches: HitchRecord[] = [];
  private buildEvents: { timestamp: number; durationMs: number }[] = [];

  // Configuration
  private slowNodeThresholdMs = 30;
  private maxSlowNodesStored = 50;
  private maxTopNodesStored = 50;
  private maxHitchesStored = 50;
  private hitchWindowMs = 100;
  private hitchDurationThresholdMs = 50;

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }

  isEnabled() {
    return this.enabled;
  }

  reset() {
    this.totalBuildTimeMs = 0;
    this.nodeBuildCount = 0;
    this.maxSingleNodeBuildTimeMs = 0;
    this.lastRootBuildTimeMs = 0;
    this.totalConfigPhaseMs = 0;
    this.totalChildrenPhaseMs = 0;
    this.maxConfigPhaseMs = 0;
    this.maxChildrenPhaseMs = 0;
    this.slowNodes.length = 0;
    this.topNodes.length = 0;
    this.hitches.length = 0;
    this.buildEvents.length = 0;
    this.distinctNodeIds.clear();
  }

  recordBuild(params: {
    id: number;
    typeId: unknown;
    nodeDuration: number;
    configPhaseMs: number;
    childrenPhaseMs: number;
    childSlotNames: string[];
  }) {
    if (!this.enabled) return;

    const { id, typeId, nodeDuration, configPhaseMs, childrenPhaseMs, childSlotNames } = params;

    this.totalBuildTimeMs += nodeDuration;
    this.totalConfigPhaseMs += configPhaseMs;
    this.totalChildrenPhaseMs += childrenPhaseMs;
    this.nodeBuildCount += 1;
    this.distinctNodeIds.add(id);

    if (nodeDuration > this.maxSingleNodeBuildTimeMs) this.maxSingleNodeBuildTimeMs = nodeDuration;
    if (configPhaseMs > this.maxConfigPhaseMs) this.maxConfigPhaseMs = configPhaseMs;
    if (childrenPhaseMs > this.maxChildrenPhaseMs) this.maxChildrenPhaseMs = childrenPhaseMs;

    // Record slow nodes
    if (nodeDuration >= this.slowNodeThresholdMs) {
      this.slowNodes.push({
        id,
        typeId,
        durationMs: nodeDuration,
        configPhaseMs,
        childrenPhaseMs,
        childSlotNames,
        timestamp: Date.now(),
      });
      if (this.slowNodes.length > this.maxSlowNodesStored) this.slowNodes.shift();
    }

    // Maintain topNodes (largest durations)
    const record: SlowNodeRecord = {
      id,
      typeId,
      durationMs: nodeDuration,
      configPhaseMs,
      childrenPhaseMs,
      childSlotNames,
      timestamp: Date.now(),
    };
    if (this.topNodes.length < this.maxTopNodesStored) {
      this.topNodes.push(record);
    } else {
      // Find smallest duration in current top list
      let smallestIndex = 0;
      let smallestDuration = this.topNodes[0].durationMs;
      for (let i = 1; i < this.topNodes.length; i++) {
        if (this.topNodes[i].durationMs < smallestDuration) {
          smallestDuration = this.topNodes[i].durationMs;
          smallestIndex = i;
        }
      }
      if (nodeDuration > smallestDuration) {
        this.topNodes[smallestIndex] = record;
      }
    }
    // Keep list sorted descending for convenience
    this.topNodes.sort((a, b) => b.durationMs - a.durationMs);

    // Hitch detection: update rolling window
    const now = performance.now();
    this.buildEvents.push({ timestamp: now, durationMs: nodeDuration });
    // Remove events outside window
    const windowStart = now - this.hitchWindowMs;
    while (this.buildEvents.length && this.buildEvents[0].timestamp < windowStart) {
      this.buildEvents.shift();
    }
    const windowDuration = this.buildEvents.reduce((sum, e) => sum + e.durationMs, 0);
    if (windowDuration >= this.hitchDurationThresholdMs) {
      const lastHitch = this.hitches[this.hitches.length - 1];
      // Avoid recording duplicates for consecutive builds within same window span
      if (!lastHitch || now - lastHitch.timestamp > this.hitchWindowMs / 2) {
        this.hitches.push({
          timestamp: Date.now(),
          windowDurationMs: windowDuration,
          buildCount: this.buildEvents.length,
        });
        if (this.hitches.length > this.maxHitchesStored) this.hitches.shift();
      }
    }
  }

  setLastRootBuildTime(timeMs: number) {
    if (!this.enabled) return;
    this.lastRootBuildTimeMs = timeMs;
  }

  getStats(): DesignerNodeBuildPerfStats {
    return {
      totalBuildTimeMs: this.totalBuildTimeMs,
      nodeBuildCount: this.nodeBuildCount,
      distinctNodeCount: this.distinctNodeIds.size,
      averageNodeBuildTimeMs: this.nodeBuildCount ? this.totalBuildTimeMs / this.nodeBuildCount : 0,
      maxSingleNodeBuildTimeMs: this.maxSingleNodeBuildTimeMs,
      lastRootBuildTimeMs: this.lastRootBuildTimeMs,
      totalConfigPhaseMs: this.totalConfigPhaseMs,
      totalChildrenPhaseMs: this.totalChildrenPhaseMs,
      averageConfigPhaseMs: this.nodeBuildCount ? this.totalConfigPhaseMs / this.nodeBuildCount : 0,
      averageChildrenPhaseMs: this.nodeBuildCount ? this.totalChildrenPhaseMs / this.nodeBuildCount : 0,
      maxConfigPhaseMs: this.maxConfigPhaseMs,
      maxChildrenPhaseMs: this.maxChildrenPhaseMs,
    };
  }

  getStatsWithSlowNodes(): DesignerNodeBuildPerfStatsWithSlowNodes {
    return {
      ...this.getStats(),
      slowNodes: this.slowNodes.slice(),
      topNodes: this.topNodes.slice(),
      hitches: this.hitches.slice(),
    };
  }

  getSlowNodes() {
    return this.slowNodes.slice();
  }

  setSlowNodeThreshold(ms: number) {
    this.slowNodeThresholdMs = ms;
  }

  setMaxSlowNodesStored(n: number) {
    this.maxSlowNodesStored = n;
  }

  setMaxTopNodesStored(n: number) {
    this.maxTopNodesStored = n;
  }

  setHitchWindowMs(ms: number) {
    this.hitchWindowMs = ms;
  }

  setHitchDurationThresholdMs(ms: number) {
    this.hitchDurationThresholdMs = ms;
  }

  setMaxHitchesStored(n: number) {
    this.maxHitchesStored = n;
  }
}

// Singleton instance
export const profiler = new BuildDesignerNodeProfiler();

// Public API
export const enableDesignerNodeProfiling = () => profiler.enable();
export const disableDesignerNodeProfiling = () => profiler.disable();
export const resetDesignerNodeBuildPerfStats = () => profiler.reset();
export const getDesignerNodeBuildPerfStats = () => profiler.getStats();
export const getDesignerNodeBuildPerfStatsWithSlowNodes = () => profiler.getStatsWithSlowNodes();
export const getDesignerNodeSlowNodes = () => profiler.getSlowNodes();
export const setDesignerNodeSlowNodeThreshold = (ms: number) => profiler.setSlowNodeThreshold(ms);
export const setDesignerNodeMaxSlowNodesStored = (n: number) => profiler.setMaxSlowNodesStored(n);
export const setDesignerNodeMaxTopNodesStored = (n: number) => profiler.setMaxTopNodesStored(n);
export const setDesignerNodeHitchWindowMs = (ms: number) => profiler.setHitchWindowMs(ms);
export const setDesignerNodeHitchDurationThresholdMs = (ms: number) => profiler.setHitchDurationThresholdMs(ms);
export const setDesignerNodeMaxHitchesStored = (n: number) => profiler.setMaxHitchesStored(n);
